import {
  CountByActivationSelfConfirmed,
  CountByDeny,
  Page,
  PageRequest,
  toPageSearchParams,
} from "../../common/pagination";
import { baseApi, OrganizationRequest } from "./baseApi";
import { GridSelectionModel } from "@mui/x-data-grid";
import {
  ExternalAuthorizeEventResult,
  ExternalAuthorizeEventStatusReason,
} from "./ExternalAuthorizeEvent";
import { toSearchParams } from "./util";

export interface Associate {
  publicId: string;
  name?: string;
  emailAddress?: string;
  deny?: boolean;
  externalLastLoginStatus?: LastLoginStatus;
  activationSelfConfirmed: boolean;
  admin?: boolean;
}

export interface LastLoginStatus {
  result?: ExternalAuthorizeEventResult;
  failReason?: ExternalAuthorizeEventStatusReason;
}

export enum InvitationStatus {
  NOT_SENT = "NOT_SENT",
  SENT = "SENT",
  ACCEPTED = "ACCEPTED",
}

export enum AssociateValidationError {
  UNKNOWN = "UNKNOWN",
  DUPLICATE_VIN = "DUPLICATE_VIN",
  INVALID_VIN = "INVALID_VIN",
  MISSING_VIN = "MISSING_VIN",
  LICENSE_PLATE_SIZE = "LICENSE_PLATE_SIZE",
  DESCRIPTION_SIZE = "DESCRIPTION_SIZE",
}

export interface AssociateValidationErrors {
  [email: string]: AssociateValidationError[];
}

interface DenyRequest extends OrganizationRequest {
  publicId?: string;
  deny: boolean;
}

interface AssociateIdRequest extends OrganizationRequest {
  id: string;
}

interface AssociateBodyRequest extends OrganizationRequest {
  associate: Partial<Associate>;
}

interface ImportAssociatesRequest extends OrganizationRequest {
  file: File;
}

interface DeleteAssociatesRequest extends OrganizationRequest {
  ids: GridSelectionModel;
}

export const associatesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssociates: builder.query<Page<Associate[]>, PageRequest<Associate>>({
      query: (request) => {
        return `/associates?${toPageSearchParams(request)}`;
      },
      providesTags: (_) => ["Associate"],
    }),
    getAssociateById: builder.query<Associate, AssociateIdRequest>({
      query: ({ id, organizationId }) =>
        `/associates/${id}?${toSearchParams({ organizationId })}`,
      providesTags: (_) => ["Associate"],
    }),
    deleteAssociateById: builder.mutation<void, AssociateIdRequest>({
      query: ({ id, organizationId }) => ({
        url: `/associates/${id}?${toSearchParams({ organizationId })}`,
        method: "DELETE",
      }),
      invalidatesTags: (_) => ["Associate", "AssociateStats"],
    }),
    deleteAssociatesByIds: builder.mutation<void, DeleteAssociatesRequest>({
      query: ({ ids, organizationId }) => ({
        url: `/associates?${toSearchParams({
          ids,
          organizationId,
        })}`,
        method: "DELETE",
      }),
      invalidatesTags: (_) => ["Associate", "AssociateStats"],
    }),
    createAssociate: builder.mutation<void, AssociateBodyRequest>({
      query: ({ associate, organizationId }) => ({
        url: `/associates?${toSearchParams({ organizationId })}`,
        method: "POST",
        body: associate,
      }),
      invalidatesTags: (_) => ["Associate", "AssociateStats", "Admin"],
    }),
    updateAssociate: builder.mutation<void, AssociateBodyRequest>({
      query: ({ associate: { publicId, ...patch }, organizationId }) => {
        return {
          url: `/associates/${publicId}?${toSearchParams({ organizationId })}`,
          method: "PUT",
          body: patch,
        };
      },
      invalidatesTags: (_) => ["Associate", "AssociateStats", "Admin"],
    }),
    denyAssociate: builder.mutation<void, DenyRequest>({
      query: ({ publicId, deny, organizationId }) => {
        return {
          url: `/associates/${publicId}?${toSearchParams({ organizationId })}`,
          method: "PATCH",
          body: {
            deny,
          },
        };
      },
      invalidatesTags: (_) => ["Associate", "AssociateStats"],
    }),
    importAssociates: builder.mutation<Associate[], ImportAssociatesRequest>({
      query: ({ file, organizationId }) => {
        const formData = new FormData();
        formData.append("data", file);
        return {
          url: `/associates/import?${toSearchParams({ organizationId })}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_) => ["Associate", "AssociateStats"],
    }),

    inviteAssociate: builder.mutation<void, AssociateIdRequest>({
      query: ({ id, organizationId }) => ({
        url: `/associates/${id}/invite?${toSearchParams({ organizationId })}`,
        method: "POST",
      }),
      invalidatesTags: (_) => ["Associate"],
    }),
    countAssociatesByDeny: builder.query<CountByDeny, string>({
      query: (organizationId) =>
        `/associates/count-by-deny?${toSearchParams({ organizationId })}`,
      providesTags: (_) => ["AssociateStats"],
    }),
    countAssociatesByActivationSelfConfirmed: builder.query<
      CountByActivationSelfConfirmed,
      string
    >({
      query: (organizationId) =>
        `/associates/count-by-activation-self-confirmed?${toSearchParams({
          organizationId,
        })}`,
      providesTags: (_) => ["AssociateStats"],
    }),
  }),
});

export const {
  useGetAssociatesQuery,
  useLazyGetAssociatesQuery,
  useGetAssociateByIdQuery,
  useCreateAssociateMutation,
  useDeleteAssociateByIdMutation,
  useDeleteAssociatesByIdsMutation,
  useUpdateAssociateMutation,
  useDenyAssociateMutation,
  useImportAssociatesMutation,
  useInviteAssociateMutation,
  useCountAssociatesByDenyQuery,
  useCountAssociatesByActivationSelfConfirmedQuery,
} = associatesApi;
